import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { CanActivate, CanActivateChild, Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '@env/environment';
import { AuthenticationService } from '@src/app/core/services/Auth/authservice';
import mockClaims from '@src/app/mockdata/mockclaimsdata.json';

const TOKEN_FLAG = 'tokenProcessed';
const DATA_INIT_FLAG = 'FincompDataInitialized';
const FINCOM_DATA_KEY = 'fincomData';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, CanActivateChild {
  private readonly isBrowser: boolean;

  constructor(
    private router: Router,
    private authService: AuthenticationService,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  async canActivate(): Promise<boolean> {
    if (!this.isBrowser) return true;

    const { tokenParam, tokenAlreadyProcessed } = this.parseTokenInfo();

    // Localhost mock initialization
    this.initializeMockDataIfNeeded();
    // Token-based login flow
    if (tokenParam && !tokenAlreadyProcessed) {
      return this.authService.handleTokenLogin(tokenParam);
    }

    // Session or silent SSO
    if (await this.checkSessionOrSilentSSO()) {
      return true;
    }

    // Final redirect handling
    return this.handleUnauthenticatedRedirect(
      tokenParam,
      tokenAlreadyProcessed,
    );
  }

  canActivateChild(): Promise<boolean> {
    return this.canActivate();
  }

  // -------------------- Helpers --------------------

  private initializeMockDataIfNeeded(): void {
    if (window.location.hostname !== 'localhost') return;

    const initialized = sessionStorage.getItem(DATA_INIT_FLAG);
    if (!initialized) {
      sessionStorage.setItem(FINCOM_DATA_KEY, JSON.stringify(mockClaims));
      sessionStorage.setItem(DATA_INIT_FLAG, 'true');
    }
  }

  private async checkSessionOrSilentSSO(): Promise<boolean> {
    try {
      const sessionRes = await fetch(`${environment.apiUrl}/session`, {
        credentials: 'include',
      });

      if (sessionRes.ok) {
        await this.populateUserSession(sessionRes);
        this.authService.completeAuthCheck();
        return true;
      }

      // Silent SSO fallback
      const silentRes = await fetch(
        `${environment.apiUrl.replace(/\/api$/, '')}/silent`,
        { credentials: 'include' },
      );

      if (silentRes.status === 204) {
        this.authService.completeAuthCheck();
        return true;
      }
    } catch (err) {
      console.warn('[AUTH] Session check failed:', err);
    }

    return false;
  }

  private async populateUserSession(sessionRes: Response): Promise<void> {
    if (window.location.hostname === 'localhost') {
      const storedData = sessionStorage.getItem(FINCOM_DATA_KEY);
      const claims = storedData ? JSON.parse(storedData) : mockClaims;
      this.authService.setSessionData(claims.user);
      this.authService.setComitteesData(claims.user.committees || []);
      return;
    }

    const sessionData = await sessionRes.json();
    try {
      const res = await fetch(
        `${environment.userCommittes}/${sessionData.user.sub}/profile?include=20groupcommitees`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${sessionData.tokens.access_token}`,
          },
        },
      );
      if (res.status === 200) {
        const profileData = await res.json();
        const userData = {
          email: profileData.email,
          name: profileData.name,
          sub: profileData.sub,
          committees: profileData['20groupcommitees'] || [],
        };
        this.authService.setSessionData(userData);
        this.authService.setComitteesData(userData.committees || []);
        console.log('[AUTH] User session initialized with profile data');
      } else {
        console.log(
          '[AUTH] Failed to fetch profile data, falling back to session data',
        );
        this.authService.setSessionData(sessionData.user);
        this.authService.setComitteesData(sessionData.user.committees || []);
      }
    } catch {
      console.log(
        '[AUTH] Failed to fetch profile data, falling back to session data',
      );
      this.authService.setSessionData(sessionData.user);
      this.authService.setComitteesData(sessionData.user.committees || []);
    }

    await this.monitorTokenExpiry(sessionData?.tokens?.access_token);
  }

  private async monitorTokenExpiry(token?: string): Promise<void> {
    if (!token) return;

    try {
      const decoded = this.authService.decodeToken(token);
      if (!decoded?.exp) return;

      if (this.isExpired(decoded.exp)) {
        await this.triggerSilentLogin();
      }
    } catch (error) {
      console.error('[AUTH] Token decode failed:', error);
    }
  }

  private isExpired(exp: number): boolean {
    return Math.floor(Date.now() / 1000) >= exp;
  }

  private async handleUnauthenticatedRedirect(
    tokenParam: string | null,
    alreadyProcessed: boolean,
  ): Promise<boolean> {
    const cameFromExternal =
      !tokenParam &&
      !alreadyProcessed &&
      !document.referrer?.includes('nada.org');

    if (cameFromExternal) {
      window.location.href = environment.redirectGroupUrl;
      return false;
    }

    this.authService.completeAuthCheck();
    await this.router.navigate(['/login']);
    return false;
  }

  private parseTokenInfo() {
    const url = new URL(window.location.href);
    return {
      tokenParam: url.searchParams.get('token'),
      tokenAlreadyProcessed: sessionStorage.getItem(TOKEN_FLAG) === 'true',
    };
  }

  private async triggerSilentLogin(): Promise<void> {
    try {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = `${environment.apiUrl}/silent`;
      document.body.appendChild(iframe);
    } catch (error) {
      console.error('[AUTH] Silent login failed:', error);
    }
  }
}
